import React, { VFC, useState, useMemo } from 'react';
import clsx from 'clsx';
import { Box, CardGeneric, CardHeading, LabelValue, makeStyles, Theme } from '@dxb-web-ui-framework/design-system';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { ErrorAlert } from '@dxb-web-ui-framework/common/components/ErrorAlert';
import { AxiosError } from 'axios';
import { AccountBalance } from '@dxb-web-ui-framework/design-system/dist/icons';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '@dxb-web-ui-framework/common/utils/Math';
import { invokeService } from '@dxb-web-ui-framework/common/hooks';
import { useUserInfo } from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { TransferServiceImpl } from '../../../../../fundTransfer/src/domain/Transfer/usecases';
import { RequestDealerRate } from '../../../../../fundTransfer/src/components/TransferStepper';
import { Reject } from '../../../../../authorizations/src/components/Reject';
import { Finalize } from '../../../../../authorizations/src/components/Finalize';
import { ExchangeCalculateResponseBody } from '../../../../../fundTransfer/src/domain/Transfer/entities/ExchangeCalculateResponseDTO';
import { DealerRatesWithCalculatedTimer } from '../../../../../fundTransfer/src/domain/Transfer/entities/DealerRatesResponseDTO';
import { useLanguage } from '../../../../../internationalization/src/contexts/Internationalization/hooks';
import { SkeletonTypography } from '../../../../../content-composer/src/components/Generic/Skeleton';
import { MandateRequestRateBtn } from '../../../../../authorizations/src';
import { calculateExchangeRate } from '../../../../../fundTransfer/src/utils/utils';
import { ConsolidatedTransactionDetails } from '../../../domain/Transaction/entities/ConsolidatedTransaction';
import { DealerRateCounter } from '../TransactionDetailsDealerRate';
import { AcceptAndFinalize } from '../TransactionDetailsAuthorization';
import { ResidentBankLogo } from '../ResidentBankLogo';
import { useRefetchConsolidatedTransactionList } from '../../../contexts/ConsolidatedTransactions/hooks';
import { useRefetchConsolidatedTransactionDetails } from '../../../contexts/ConsolidatedTransactionDetails/hooks';

const useStyles = makeStyles<Theme>(({ palette, spacing, breakpoints }) => ({
  defaultBackgroundColor: {
    backgroundColor: palette.background.default,
  },
  dealerActionsContainer: {
    display: 'flex',
    gap: spacing(4),
    flexWrap: 'wrap',
    [breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: spacing(3),
      marginBottom: spacing(3),
    },
  },
  labelValuesContainer: {
    display: 'flex',
    gap: spacing(4),
    flexWrap: 'wrap',
    marginBottom: spacing(4),
    [breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: spacing(3),
      marginBottom: spacing(3),
    },
  },
  bankAccountValue: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing(2),
    '& span': {
      flex: 1,
    },
    '& span:nth-child(2)': {
      '& span:first-child': {
        wordBreak: 'break-all',
        display: 'block',
        flex: 1,
      },
    },
  },
  bankNameContainer: {
    padding: spacing(1),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing(1),
  },
}));

type TransactionInvolvedPartiesProps = {
  transactionDetails: ConsolidatedTransactionDetails;
  isStandalonePage?: boolean;
  isLoading: boolean;
  isLoadingDealerRates?: boolean;
  needMandate: boolean;
  dealerRates?: DealerRatesWithCalculatedTimer;
  isReadyForDealerRateAcceptance?: boolean;
  doesUserAwaitsForDealerRateAcceptance?: boolean;
  fundTransfersService?: TransferServiceImpl;
  exchangeCalculateData?: ExchangeCalculateResponseBody;
  shouldShowMandateRequestRateButton?: boolean;
  shouldShowApproximateRate?: boolean;
};

const TransactionInvolvedParties: VFC<TransactionInvolvedPartiesProps> = ({
  transactionDetails,
  isStandalonePage,
  isLoading,
  isLoadingDealerRates,
  needMandate,
  dealerRates,
  isReadyForDealerRateAcceptance,
  fundTransfersService,
  doesUserAwaitsForDealerRateAcceptance,
  exchangeCalculateData,
  shouldShowMandateRequestRateButton,
  shouldShowApproximateRate,
}) => {
  const [shouldResetTimer, setShouldResetTimer] = useState(false);
  const [isPostingData, setIsPostingData] = useState(false);
  const [postDataError, setPostDataError] = useState<AxiosError<APIGatewayResponse<{}>, any> | undefined>(undefined);
  const [isAcceptanceBtnDisabled, setIsAcceptanceBtnDisabled] = useState(false);
  const language = useLanguage();
  const numberFormat = language.cultureName;
  const { t } = useTranslation();
  const classes = useStyles();
  const userInfo = useUserInfo();

  const onRefetchTransactionDetails = useRefetchConsolidatedTransactionDetails();
  const onRefetchTransactionsList = useRefetchConsolidatedTransactionList();

  const showExchangeRateLabelValue =
    (transactionDetails?.accountCurrency !== transactionDetails?.exchangeCurrency ||
      (transactionDetails?.internalDestinationCurrencyId !== 0 &&
        transactionDetails?.internalDestinationCurrencyShortDescr !== transactionDetails?.accountCurrency)) &&
    transactionDetails?.type !== 'I' &&
    transactionDetails?.status !== '98';

  const renderBeneficiaryBankName = () => {
    if (transactionDetails?.bankName.match(/ecommbx.*/i)) {
      return <ResidentBankLogo resident={'EcommBX'} />;
    }
    return (
      <Box justifyContent={isStandalonePage ? 'flex-start' : 'flex-end'} alignItems={'center'}>
        <Box className={clsx(classes.defaultBackgroundColor, classes.bankNameContainer)}>
          <AccountBalance />
          <Box flex={1}>{transactionDetails?.bankName && transactionDetails?.bankName?.toUpperCase()}</Box>
        </Box>
      </Box>
    );
  };

  const onDealerRateExpiration = () => {
    setIsAcceptanceBtnDisabled(true);
    setShouldResetTimer(true);
  };

  const onClickNext = async (e: any) => {
    if (fundTransfersService) {
      setIsPostingData(true);
      setPostDataError(undefined);
      const { error } = await invokeService<{}>(() =>
        fundTransfersService?.RequestNewDealerRate({
          orderCode: String(transactionDetails.orderId),
          nextCallback: () => {
            onRefetchTransactionsList();
            onRefetchTransactionDetails();
          },
        }),
      );
      setIsPostingData(false);
      setPostDataError(error);
    }
  };

  const onReject = async (e: any) => {
    setShouldResetTimer(true);
    return;
    // if (fundTransfersService) {
    //   setIsPostingData(true);
    //   setPostDataError(undefined);
    //   const { error } = await invokeService<{}>(() =>
    //     fundTransfersService.RejectDealerRate({
    //       orderCode: String(transactionDetails.orderId),
    //       nextCallback: () => {
    //         onRefetchTransactionsList();
    //         onRefetchTransactionDetails();
    //       },
    //     }),
    //   );
    //   setIsPostingData(false);
    //   setPostDataError(error);
    // }
  };

  const onRequestDealerUserSelection: React.MouseEventHandler<HTMLButtonElement> = e => {
    onClickNext(e);
  };

  const calculatedExchangeRateValues = () => {
    const exchangeCurrency =
      transactionDetails?.exchangeCurrency === transactionDetails?.accountCurrency &&
      transactionDetails?.internalDestinationCurrencyShortDescr.length
        ? transactionDetails?.internalDestinationCurrencyShortDescr
        : transactionDetails?.exchangeCurrency;
    const { firstCurrency, firstAmount, secondAmount, secondCurrency } = calculateExchangeRate(
      exchangeCurrency,
      transactionDetails.accountCurrency,
      transactionDetails.rateCurrencyISO,
      dealerRates
        ? dealerRates.currencyRate
        : transactionDetails && transactionDetails?.trxCollectedAmount !== 0
        ? transactionDetails?.trxRateGiven
        : exchangeCalculateData
        ? exchangeCalculateData.rate
        : 0,
    );

    const firstUnit = formatAmount(firstAmount, firstCurrency, 2, numberFormat);
    const secondUnit = formatAmount(secondAmount, secondCurrency, 4, numberFormat, false, 'floor');

    return `${firstUnit} ${Boolean(shouldShowApproximateRate) && dealerRates === undefined ? '≈' : '='} ${secondUnit}`;
  };

  const isLastModifier =
    !!userInfo?.customerId &&
    !!transactionDetails &&
    transactionDetails?.lastModifyCustomerId &&
    transactionDetails?.lastModifyCustomerId === userInfo?.customerId;

  const shouldShowFinalizeButton = !!transactionDetails && transactionDetails?.status === '0' && !needMandate;
  const isFromCurrencySameOrderCurrency =
    transactionDetails?.exchangeCurrency === transactionDetails?.accountCurrency &&
    transactionDetails?.internalDestinationCurrencyId !== 0 &&
    transactionDetails?.internalDestinationCurrencyId !== transactionDetails?.fromCurrencyId;

  return (
    <>
      <Box paddingTop={isStandalonePage ? 0 : 4} paddingBottom={3}>
        <SkeletonTypography
          hasSkeleton={isLoading}
          skeletonProps={{ variant: 'text' }}
          typographyProps={{ variant: isStandalonePage ? 'h5' : 'h4' }}>
          {isLoading && '-'}
          {transactionDetails &&
            formatAmount(
              isReadyForDealerRateAcceptance && dealerRates
                ? isFromCurrencySameOrderCurrency
                  ? dealerRates.convertedBoughtAmount
                  : dealerRates.convertedSoldAmount
                : transactionDetails.amount,
              transactionDetails.exchangeCurrency,
              2,
              numberFormat,
            )}
        </SkeletonTypography>
      </Box>
      {isLoading && (
        <Box className={clsx({ [classes.labelValuesContainer]: isLoading })}>
          <LabelValue hasSkeleton={isLoading} />
          <LabelValue hasSkeleton={isLoading} />
        </Box>
      )}
      {showExchangeRateLabelValue && (
        <Box className={clsx({ [classes.labelValuesContainer]: showExchangeRateLabelValue })}>
          <LabelValue
            label={t(
              'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.ConsolidatedTransactionInvolvedParties.ExchangeAmount',
            )}
            labelAlignment='left'
            labelColor='textPrimary'
            labelSize='small'
            value={
              transactionDetails &&
              formatAmount(
                isReadyForDealerRateAcceptance && dealerRates
                  ? isFromCurrencySameOrderCurrency
                    ? dealerRates.convertedSoldAmount
                    : dealerRates.convertedBoughtAmount
                  : exchangeCalculateData?.amount ?? transactionDetails?.trxCollectedAmount,
                isFromCurrencySameOrderCurrency
                  ? transactionDetails?.internalDestinationCurrencyShortDescr
                  : transactionDetails.accountCurrency,
                2,
                numberFormat,
              )
            }
            valueAlignment='left'
            valueAreaLabel=''
            valueColor='secondary'
            valueSize='small'
            hasSkeleton={isLoading || isLoadingDealerRates}
          />
          <>
            <LabelValue
              label={t(
                'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.ConsolidatedTransactionInvolvedParties.ExchangeRate',
              )}
              labelAlignment='left'
              labelColor='textPrimary'
              labelSize='small'
              value={calculatedExchangeRateValues()}
              valueAlignment='left'
              valueAreaLabel={calculatedExchangeRateValues()}
              valueColor='secondary'
              valueSize='small'
              hasSkeleton={isLoading || isLoadingDealerRates}
            />
            {Boolean(shouldShowMandateRequestRateButton) && (
              <MandateRequestRateBtn isLoading={Boolean(isLoading || isLoadingDealerRates)} />
            )}
            {doesUserAwaitsForDealerRateAcceptance && (
              <LabelValue
                label={t('DealerRates.Pending')}
                labelAlignment='left'
                labelColor='textPrimary'
                labelSize='small'
                value=''
                valueAlignment='left'
                valueAreaLabel={t('DealerRates.Pending')}
                valueColor='secondary'
                valueSize='small'
                hasSkeleton={isLoading || isLoadingDealerRates}
              />
            )}
            {isReadyForDealerRateAcceptance && dealerRates && isLastModifier && (
              <>
                <DealerRateCounter
                  isLoading={isLoading}
                  timer={!shouldResetTimer ? dealerRates.calculatedDiffInSeconds : 0}
                  onDealerRateExpiration={onDealerRateExpiration}
                />
                {fundTransfersService && (
                  <Box className={classes.dealerActionsContainer}>
                    <AcceptAndFinalize
                      isAcceptanceBtnDisabled={isAcceptanceBtnDisabled}
                      dealerRates={dealerRates}
                      transactionDetails={transactionDetails}
                      onRefetchDetails={onRefetchTransactionDetails}
                      t={t}
                      fundTransfersService={fundTransfersService}
                      externalButtonProps={{ variant: 'contained', color: 'primary', size: 'large' }}
                    />
                    <RequestDealerRate
                      t={t}
                      onRequestDealerUserSelection={onRequestDealerUserSelection}
                      isLoading={isPostingData}
                      isDisabled={!shouldResetTimer}
                      externalButtonProps={{
                        variant: 'contained',
                        color: 'primary',
                        size: 'large',
                        title: t(
                          'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.ConsolidatedTransactionInvolvedParties.RequestNewRate',
                        ),
                      }}
                    />
                    {!shouldResetTimer && <Reject t={t} onReject={onReject} isLoading={isPostingData} />}
                  </Box>
                )}
              </>
            )}
            {/* DEPRECATED: PLEASE REMOVE IN THE FUTURE */}
            {shouldShowFinalizeButton && isLastModifier && (
              <Finalize
                onRefetchTransactionDetails={onRefetchTransactionDetails}
                t={t}
                fundTransfersService={fundTransfersService as TransferServiceImpl}
                transactionDetails={transactionDetails}
              />
            )}
          </>
        </Box>
      )}
      {postDataError && (
        <Box mb={2}>
          <ErrorAlert message={postDataError.message} />
        </Box>
      )}
      {!isStandalonePage && (
        <CardHeading
          heading={t(
            'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.ConsolidatedTransactionInvolvedParties.FromHeading',
          )}
          hideOnMobile={false}
        />
      )}
      <Box width='100%'>
        <CardGeneric spacing='dense'>
          <>
            {isStandalonePage && (
              <CardHeading
                heading={t(
                  'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.ConsolidatedTransactionInvolvedParties.FromHeading',
                )}
                hideOnMobile={false}
              />
            )}
            <Box
              className={classes.bankAccountValue}
              display='flex'
              justifyContent={isStandalonePage ? 'flex-start' : 'space-between'}
              flexDirection={isStandalonePage ? 'column' : 'row'}
              alignItems={isStandalonePage ? 'flex-start' : 'center'}>
              <LabelValue
                label={
                  transactionDetails?.type === 'O'
                    ? transactionDetails?.alias ?? transactionDetails?.accountProductDescription
                    : transactionDetails?.beneficiaryFullName
                }
                labelTestId='transfer-details-account-name-from'
                value={transactionDetails?.fromAccountIban}
                valueTestId='transfer-details-account-number-from'
                valueAreaLabel={
                  transactionDetails && String(`${userInfo?.lastname}-${transactionDetails?.fromAccountIban}`)
                }
                labelColor='textSecondary'
                labelSize='small'
                valueSize='medium'
                valueColor='primary'
                labelAlignment='left'
                valueAlignment='left'
                hasSkeleton={isLoading}
              />
              {!isLoading &&
                (transactionDetails?.type === 'O' ? (
                  <Box
                    mt={!isStandalonePage ? 1 : 0}
                    display={'flex'}
                    justifyContent={isStandalonePage ? 'flex-start' : 'flex-end'}
                    alignItems={'center'}>
                    <ResidentBankLogo resident={'EcommBX'} />
                  </Box>
                ) : (
                  <Box mt={isStandalonePage ? 2 : 0} display='flex'>
                    {renderBeneficiaryBankName()}
                  </Box>
                ))}
            </Box>
          </>
        </CardGeneric>
      </Box>

      <Box marginTop={isStandalonePage ? 2 : 3} />

      {!isStandalonePage && (
        <CardHeading
          heading={t(
            'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.ConsolidatedTransactionInvolvedParties.ToHeading',
          )}
          hideOnMobile={false}
        />
      )}
      <Box width='100%'>
        <CardGeneric spacing='dense'>
          <>
            {isStandalonePage && (
              <CardHeading
                heading={t(
                  'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.ConsolidatedTransactionInvolvedParties.ToHeading',
                )}
                hideOnMobile={false}
              />
            )}
            <Box
              className={classes.bankAccountValue}
              display='flex'
              justifyContent={isStandalonePage ? 'flex-start' : 'space-between'}
              flexDirection={isStandalonePage ? 'column' : 'row'}
              alignItems={isStandalonePage ? 'flex-start' : 'center'}>
              <LabelValue
                label={
                  transactionDetails?.type === 'O'
                    ? transactionDetails?.beneficiaryFullName ?? ''
                    : transactionDetails?.alias ?? transactionDetails?.accountProductDescription
                }
                labelTestId='transfer-details-account-name-to'
                value={
                  transactionDetails?.p2pValue ? transactionDetails?.p2pValue : transactionDetails?.toAccount ?? ''
                }
                valueTestId='transfer-details-account-number-to'
                valueAreaLabel={transactionDetails?.toAccount ?? ''}
                labelColor='textSecondary'
                labelSize='small'
                valueSize='medium'
                valueColor='primary'
                labelAlignment='left'
                valueAlignment='left'
                hasSkeleton={isLoading}
              />
              {!isLoading &&
                (transactionDetails?.type === 'O' ? (
                  <Box mt={isStandalonePage ? 2 : 0} display='flex'>
                    {renderBeneficiaryBankName()}
                  </Box>
                ) : (
                  <Box
                    mt={!isStandalonePage ? 1 : 0}
                    display={'flex'}
                    justifyContent={isStandalonePage ? 'flex-start' : 'flex-end'}
                    alignItems={'center'}>
                    <ResidentBankLogo resident={'EcommBX'} />
                  </Box>
                ))}
            </Box>
          </>
        </CardGeneric>
      </Box>
    </>
  );
};

export { TransactionInvolvedParties };
